/**
 * Region-correction calls, shared by every app that can show an uploaded script
 * — student, teacher and admin console alike, against one pair of routes.
 *
 * The client is injected for the same reason the upload API injects it: the
 * apps authenticate under different token keys, and only the caller's
 * credentials differ. `studentId` is the one other difference — a teacher or an
 * operator correcting a question on a student's attempt, which stays the
 * student's throughout. The server decides whether the caller may.
 *
 * See docs/FeatureDesigns/assignments-tests/solution-region-correction-target.md §7.
 */

import { API_BASE, handleResponse, waitForAsyncJob, type ApiClient } from '../api';
import type { ResolvedUpload } from '../session-results';
import type { RegionCorrectionRequest, RegionCorrectionResponse } from './RegionCorrector';

export interface RegionCorrectionApi {
  /** The archived pages plus each question's current regions. */
  resolveUpload(testSessionId: string, uploadId?: string): Promise<ResolvedUpload>;
  correctRegion(testSessionId: string, input: RegionCorrectionRequest): Promise<RegionCorrectionResponse>;
}

export function createRegionCorrectionApi(
  client: Pick<ApiClient, 'getAuthHeaders'>,
  /** The student whose attempt this is, when the caller is not that student. */
  studentId?: string,
  /** Where to poll the re-read job. Defaults to the user route, which only a
   *  student or teacher may call; the admin console passes its own. */
  jobUrl: (jobId: string) => string = id => `${API_BASE}/async-jobs/${encodeURIComponent(id)}`,
): RegionCorrectionApi {
  const base = (testSessionId: string) => `${API_BASE}/test-sessions/${encodeURIComponent(testSessionId)}`;

  return {
    async resolveUpload(testSessionId, uploadId) {
      // The attempt lives in the STUDENT's partition, so the owner rides along
      // on the read too — not only on the correction itself.
      const qs = new URLSearchParams({
        ...(studentId ? { studentId } : {}),
        ...(uploadId ? { uploadId } : {}),
      }).toString();
      const response = await fetch(`${base(testSessionId)}/solution-upload${qs ? `?${qs}` : ''}`, {
        headers: client.getAuthHeaders(),
      });
      return handleResponse<ResolvedUpload>(response);
    },

    async correctRegion(testSessionId, input) {
      // The correction is saved inside the request. The re-read behind it is a
      // background job (minutes of model calls that must survive the server
      // instance), so wait for it here and answer as before.
      // See docs/FeatureDesigns/solution-analysis/reevaluation-job-target.md.
      const response = await fetch(`${base(testSessionId)}/region-corrections`, {
        method: 'POST',
        headers: { ...client.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...input, ...(studentId ? { studentId } : {}), asJob: true }),
      });
      const { rerunJobId, ...saved } = await handleResponse<RegionCorrectionResponse & { rerunJobId?: string }>(response);
      if (!rerunJobId) return saved;
      const rerun = await waitForAsyncJob<RegionCorrectionResponse['rerun']>(async (id) => handleResponse(
        await fetch(jobUrl(id), { headers: client.getAuthHeaders() }),
      ), rerunJobId);
      return { ...saved, rerun };
    },
  };
}
